import { GooeyPopover, GooeyPopoverContent, GooeyPopoverTrigger, PillButton } from '@parsleyai/ui'
import { AlertTriangle, ArrowRight, ListChecks, Loader2, RotateCw } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { PlanGenPhase, usePlanGen } from '@/store'

function fmtElapsed(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

// Pill base: 38px tall, fully rounded, text-sm/600 label, nudges down on press.
const pillBase =
  'inline-flex h-[38px] items-center gap-2 rounded-full px-3.5 text-sm font-semibold whitespace-nowrap transition-colors active:translate-y-px'

function GenSpinner() {
  return <Loader2 className="size-4 shrink-0 animate-spin text-brand-green-ink motion-reduce:animate-none" aria-hidden />
}

// Indeterminate sweep used inside the processing popover.
function GenTrack() {
  return (
    <div className="relative h-1 w-full overflow-hidden rounded-full bg-brand-green/20">
      <div className="absolute inset-y-0 left-0 w-[26%] rounded-full bg-[linear-gradient(90deg,transparent,oklch(var(--brand-green-400)),oklch(var(--brand-green-600)),transparent)] animate-pgen-sweep motion-reduce:left-[10%] motion-reduce:w-[42%] motion-reduce:animate-none" />
    </div>
  )
}

// Pulsing handle shown when the processing pill is collapsed to a dot.
function GenDot() {
  return (
    <span className="relative inline-flex size-[9px] items-center justify-center">
      <span className="absolute inset-0 rounded-full bg-brand-green animate-pulsering motion-reduce:animate-none" aria-hidden />
      <span className="relative size-[9px] rounded-full bg-brand-green" />
    </span>
  )
}

// The Ready pill, extracted so it gets a FRESH mount whenever the plan enters Ready. It captures
// the one-time bounce at mount and freezes it, so a later remount sits still. A new draftId re-arms.
function ReadyPill({
  label,
  onReview,
  draftId,
  shouldPop,
  onPopped
}: {
  label: string
  onReview: () => void
  draftId: string | null
  shouldPop: boolean
  onPopped: (draftId: string) => void
}) {
  const [pop] = useState(shouldPop)
  useEffect(() => {
    if (pop && draftId) onPopped(draftId)
  }, [pop, draftId, onPopped])
  // The DS pill, not a bespoke one: `primary` at the default 38px size is the same geometry
  // this used to hand-roll, and carries the focus ring, motion-reduce press, and disabled
  // handling the local version never had.
  return (
    <PillButton
      variant="primary"
      onClick={onReview}
      icon={<ListChecks aria-hidden />}
      trailing={<ArrowRight aria-hidden />}
      className={cn(pop && 'animate-pill-pop motion-reduce:animate-none')}>
      {label}
    </PillButton>
  )
}

// App-wide indicator that a new meal plan is being generated. Mounted in both nav
// surfaces; renders nothing while idle. All lifecycle/data comes from usePlanGen.
export function PlanPill() {
  const { t } = useTranslation()
  // Popover open/collapsed state lives in the (persistent) provider, not here: PlanPill remounts
  // on every navigation (PageLayout is per-page), so component-local state would reset and re-fire
  // the auto-open on each route change. The provider owns the auto-open-once effect too.
  const {
    phase,
    elapsedSeconds,
    draftId,
    review,
    retry,
    dismissFailed,
    getHelp,
    shouldPopReadyPill,
    markReadyPillPopped,
    popoverOpen: open,
    setPopoverOpen: setOpen,
    popoverCollapsed: collapsed,
    setPopoverCollapsed: setCollapsed
  } = usePlanGen()

  if (phase === PlanGenPhase.Idle) return null

  // Ready — single-tap review, no popover. The pill bounces in once per plan (see ReadyPill).
  if (phase === PlanGenPhase.Ready) {
    return (
      <ReadyPill
        label={t('planGen.ready.pillLabel')}
        onReview={review}
        draftId={draftId}
        shouldPop={shouldPopReadyPill}
        onPopped={markReadyPillPopped}
      />
    )
  }

  // Failed — popover with retry / help / dismiss.
  if (phase === PlanGenPhase.Failed) {
    return (
      <GooeyPopover open={open} onOpenChange={setOpen} side="bottom" align="end" sideOffset={10} panelRadius={18}>
        <GooeyPopoverTrigger>
          <button
            type="button"
            className={cn(
              pillBase,
              'border-[1.5px] border-[color-mix(in_srgb,oklch(var(--red))_45%,transparent)] bg-red-wash text-red hover:border-red'
            )}>
            <AlertTriangle className="size-3.5 shrink-0" aria-hidden />
            {t('planGen.failed.pillLabel')}
          </button>
        </GooeyPopoverTrigger>
        <GooeyPopoverContent aria-label={t('planGen.failed.title')} className="w-[300px] p-5">
          <div className="flex items-center gap-2.5">
            <span className="flex size-[30px] shrink-0 items-center justify-center rounded-full bg-[color-mix(in_srgb,oklch(var(--red))_15%,transparent)] text-red">
              <AlertTriangle className="size-[15px]" aria-hidden />
            </span>
            <div className="font-display text-base font-medium leading-tight text-foreground">{t('planGen.failed.title')}</div>
          </div>
          <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{t('planGen.failed.sub')}</p>
          <div className="mt-4 flex flex-col gap-2">
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                retry()
              }}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-green-fill px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-green-fill-strong active:translate-y-px">
              <RotateCw className="size-3.5" /> {t('planGen.failed.tryAgain')}
            </button>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => {
                  setOpen(false)
                  getHelp()
                }}
                className="inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold text-muted-foreground transition-colors hover:bg-card hover:text-foreground active:translate-y-px">
                {t('planGen.failed.getHelp')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setOpen(false)
                  dismissFailed()
                }}
                className="inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold text-muted-foreground transition-colors hover:bg-card hover:text-foreground active:translate-y-px">
                {t('planGen.failed.dismiss')}
              </button>
            </div>
          </div>
        </GooeyPopoverContent>
      </GooeyPopover>
    )
  }

  // Processing — collapsed to a dot, or full pill with progress popover.
  if (collapsed) {
    return (
      <button
        type="button"
        aria-label={t('planGen.processing.showProgress')}
        onClick={() => setCollapsed(false)}
        className="inline-flex size-[38px] items-center justify-center rounded-full border-[1.5px] border-border bg-card transition-colors hover:border-brand-green-border">
        <GenDot />
      </button>
    )
  }

  return (
    <GooeyPopover open={open} onOpenChange={setOpen} side="bottom" align="end" sideOffset={10} panelRadius={18}>
      <GooeyPopoverTrigger>
        <button
          type="button"
          className={cn(pillBase, 'border-[1.5px] border-border bg-card text-foreground hover:border-brand-green-border')}>
          <GenSpinner />
          {t('planGen.processing.pillLabel')}
          <span className="font-mono tabular-nums text-faint">{fmtElapsed(elapsedSeconds)}</span>
        </button>
      </GooeyPopoverTrigger>
      <GooeyPopoverContent aria-label={t('planGen.processing.title')} className="w-[300px] p-5">
        <div className="flex items-center gap-2.5">
          <GenSpinner />
          <div className="font-display text-base font-medium leading-tight text-foreground">{t('planGen.processing.title')}</div>
        </div>
        <div className="mt-3.5">
          <GenTrack />
        </div>
        <div className="mt-3 flex items-center justify-between text-xs">
          <span className="text-muted-foreground">{t('planGen.processing.elapsed')}</span>
          <b className="font-mono font-medium tabular-nums text-foreground">{fmtElapsed(elapsedSeconds)}</b>
        </div>
        <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{t('planGen.processing.sub')}</p>
        <div className="mt-3.5 flex justify-end">
          <button
            type="button"
            onClick={() => {
              setOpen(false)
              setCollapsed(true)
            }}
            className="inline-flex items-center gap-2 rounded-full border-[1.5px] border-border bg-white px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:border-brand-green-border hover:bg-block-bg active:translate-y-px dark:bg-surface">
            {t('planGen.processing.minimize')}
          </button>
        </div>
      </GooeyPopoverContent>
    </GooeyPopover>
  )
}
